import React, { Component } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "reactstrap";
import { CountryList } from "./user/banks/CountryList/CountryList";
import { BanksList } from "./user/banks/List/BanksList";
import { SelectedBanksList } from "./user/banks/Selected/SelectedBanks";
import Snackbar from "@mui/material/Snackbar";
import { RouteComponentProps } from "react-router";
import {
  BanksService,
  CountryMetric,
  User,
  UserBank,
  UsersAdminService,
  UserService,
} from "../generated";
import { EditUserBankModal } from "./user/banks/EditUserBankModal";

export interface Props
  extends RouteComponentProps<{
    userEmail: string;
  }> {
  isAdminMode: boolean;
}

export interface State {
  selectedCountryCode: string;
  countries: CountryMetric[];
  countriesLoading: boolean;
  banks: UserBank[];
  banksLoading: boolean;
  banksLoaded: boolean;
  selectedBanks: UserBank[];
  selectedBanksLoading: boolean;
  snackBarOpen: boolean;
  snackBarAlertSeverity: string;
  snackBarAlertMessage: string;
  userEmail: string;
  user: User;
  userNotFound: boolean;
  editedBank: UserBank;
  loading: boolean;
  logged: boolean;
}

export class ManageUsersBanks extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedCountryCode: "DK",
      countries: [],
      countriesLoading: true,
      banks: [],
      banksLoading: false,
      banksLoaded: false,
      selectedBanks: [],
      selectedBanksLoading: false,
      snackBarOpen: false,
      snackBarAlertSeverity: "",
      snackBarAlertMessage: "",
      userEmail: this.props.match?.params.userEmail,
      user: null,
      userNotFound: false,
      editedBank: null,
      loading: true,
      logged: undefined,
    };

    this.saveBank = this.saveBank.bind(this);
  }

  async getCountries() {
    this.setState({
      countriesLoading: true,
    });
    const result = this.props.isAdminMode
      ? await UsersAdminService.usersAdminGetCountries()
      : await BanksService.banksGetCountries();

    this.setState({
      countriesLoading: false,
      countries: result.sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  async getBanks(countryCode: string) {
    this.setState({
      selectedCountryCode: countryCode,
      banksLoading: true,
      banksLoaded: false,
    });

    const allBanks = await BanksService.banksGetList({
      countryCode,
    });

    this.setState({
      banksLoaded: true,
      banksLoading: false,
      banks: allBanks,
    });
  }

  async getSelectedBanksOnly() {
    this.setState({
      selectedBanksLoading: true,
    });

    const result = this.props.isAdminMode
      ? await UsersAdminService.banksGetSelected({
          email: this.state.userEmail,
        })
      : await BanksService.banksGetSelected({ email: this.state.user.email });

    this.setState({
      selectedBanksLoading: false,
      selectedBanks: result,
    });
  }

  async getUserDetails() {
    try {
      if (this.props.isAdminMode) {
        const user = await UsersAdminService.usersAdminGetUser({
          email: this.state.userEmail,
        });

        this.setState({
          user: user,
          userNotFound: false,
          loading: false,
        });
      } else {
        const user = await UserService.userGetUser();

        this.setState({
          user: user.user,
          userNotFound: false,
          loading: false,
        });
      }
    } catch {
      this.setState({
        userNotFound: true,
        loading: false,
      });
    }
  }

  async checkIfUserIsAdded() {
    const r = await UserService.userUserExists();

    this.setState({
      loading: false,
      logged: r,
    });
  }

  async componentDidMount() {
    if (this.props.isAdminMode) {
      await this.getUserDetails();
      await this.getSelectedBanksOnly();
    } else {
      await this.checkIfUserIsAdded();
      await this.getUserDetails();
      await this.getSelectedBanksOnly();
    }
  }

  async componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ) {
    if (
      prevState.user !== this.state.user &&
      this.state.user !== null &&
      this.state.user !== undefined
    ) {
      await this.getCountries();
      await this.getBanks(this.state.selectedCountryCode);
    }
  }

  handleCountryListClick = async (countryCode: string) => {
    this.setState({
      selectedCountryCode: countryCode,
    });
    await this.getBanks(countryCode);
  };

  handleUnselectBankClick = async (bank: UserBank) => {
    this.setState({
      banksLoading: true,
      selectedBanksLoading: true,
    });

    try {
      if (this.props.isAdminMode) {
        UsersAdminService.banksRemoveSelection({
          unselectedBank: bank,
          email: this.state.userEmail,
        });
      } else {
        await BanksService.banksRemoveSelection({
          unselectedBank: bank,
          email: this.state.user.email,
        });
      }

      this.handleOpenSnackBar(
        (bank.displayName ?? bank.legalName) + " unselected",
        "success"
      );
      this.unselectBank(bank);
    } catch {
      this.handleOpenSnackBar("Something went wrong", "danger");
    }

    this.setState({
      banksLoading: false,
      selectedBanksLoading: false,
    });
  };

  handleSelectBankClick = async (bank: UserBank) => {
    this.setState({
      banksLoading: true,
      selectedBanksLoading: true,
    });
    try {
      this.props.isAdminMode
        ? await UsersAdminService.usersAdminSelectBankForUser({
            bank,
            email: this.state.userEmail,
          })
        : await BanksService.banksSelectBank({
            bank,
            email: this.state.user.email,
          });

      this.handleOpenSnackBar(
        (bank.displayName ?? bank.legalName) + " added",
        "success"
      );

      const selectedBanks = this.props.isAdminMode
        ? await UsersAdminService.banksGetSelected({
            email: this.state.userEmail,
          })
        : await BanksService.banksGetSelected({
            email: this.state.user.email,
          });

      this.setState({
        selectedBanks: selectedBanks,
        banksLoading: false,
        selectedBanksLoading: false,
      });
    } catch {
      this.handleOpenSnackBar("Something went wrong", "danger");
    }
  };

  handleOpenSnackBar(alertMessage: string, alertSeverity: string) {
    this.setState({
      snackBarAlertSeverity: alertSeverity,
      snackBarAlertMessage: alertMessage,
      snackBarOpen: true,
    });
  }

  handleCloseSnackBar = () => {
    this.setState({
      snackBarOpen: false,
    });
  };

  selectBank(bank: UserBank) {
    let bankToAdd = bank;
    bankToAdd.selected = true;

    let banks = [...this.state.banks];
    let index = banks.findIndex(
      (b) => b.providerId === bank.providerId && b.id === bank.id
    );
    banks[index] = bankToAdd;

    this.state.selectedBanks.push(bankToAdd);

    this.setState({
      selectedBanks: this.state.selectedBanks,
      banks: banks,
    });
  }

  unselectBank(bank: UserBank) {
    let bankToEdit = bank;
    bankToEdit.selected = false;

    let editedBanks = [...this.state.banks];
    let index = editedBanks.findIndex(
      (b) => b.providerId === bank.providerId && b.id === bank.id
    );
    editedBanks[index] = bankToEdit;

    let editedSelectedBanks = [...this.state.selectedBanks];
    let indexSelectedBanks = editedSelectedBanks.findIndex(
      (b) => b.providerId === bank.providerId && b.id === bank.id
    );
    editedSelectedBanks.splice(indexSelectedBanks, 1);

    this.setState({
      selectedBanks: editedSelectedBanks,
      banks: editedBanks,
    });
  }

  async saveBank(bank: UserBank) {
    this.props.isAdminMode
      ? await UsersAdminService.usersAdminEditBankForUser({
          bank: bank,
          email: this.state.userEmail,
        })
      : await BanksService.banksEditBank({
          editedBank: bank,
        });

    this.setState({
      selectedBanksLoading: true,
    });

    const selectedBanks = this.props.isAdminMode
      ? await UsersAdminService.banksGetSelected({
          email: this.state.userEmail,
        })
      : await BanksService.banksGetSelected({
          email: this.state.user.email,
        });

    this.setState({
      selectedBanksLoading: false,
      editedBank: null,
      selectedBanks,
    });
  }

  editBank(bank: UserBank) {
    bank.iban ??= "";
    bank.bban ??= "";
    this.setState({ editedBank: bank });
  }

  render() {
    if (this.state.loading) {
      return <Spinner />;
    }

    return this.state.userNotFound === true ? (
      <div>
        <h3>User not found</h3>
        <span
          className="btn border px-4 btn-primary mt-5 float-right"
          onClick={() => this.props.history.goBack()}
        >
          Back
        </span>
      </div>
    ) : this.state.user === null && this.props.isAdminMode ? (
      <Spinner />
    ) : (
      <Container>
        <Col>
          <Row>
            {this.props.isAdminMode && (
              <Col>
                You are editing banks on behalf of{" "}
                <strong>
                  {this.state.user.firstName} {this.state.user.lastName}
                </strong>{" "}
                ({this.state.user.email})
              </Col>
            )}
            {!this.props.isAdminMode && (
              <Col>
                <p style={{ marginBottom: "15px" }}>
                  Please select all the banks you have account at and want to
                  use for testing purposes.
                </p>
                <p style={{ marginTop: "30px" }}>
                  Can&apos;t you find your bank on the list? Are you not sure
                  which bank to choose? Do you have any questions?
                  <a href={"mailto:providers@nordicapigateway.com"}>
                    {" "}
                    providers@nordicapigateway.com
                  </a>
                </p>
              </Col>
            )}
          </Row>
          <Row>
            <Col lg={3} s={12}>
              <Row className={"border rounded rounded-more p-2"}>
                <h3>Select banks:</h3>
                {this.state.countriesLoading ? (
                  <Spinner />
                ) : (
                  <CountryList
                    countries={this.state.countries}
                    selectedElement={this.state.selectedCountryCode}
                    handleListItemClick={this.handleCountryListClick}
                  />
                )}
                {this.state.banksLoading ? (
                  <Spinner />
                ) : (
                  <BanksList
                    banks={this.state.banks}
                    onClick={this.handleSelectBankClick}
                    hidden={!this.state.banksLoaded}
                  />
                )}
              </Row>
            </Col>
            <Col lg={9} s={12}>
              <Row
                style={{ marginLeft: "0.5rem" }}
                className={"border rounded rounded-more p-2"}
              >
                {this.props.isAdminMode && (
                  <h3>
                    Banks added to{" "}
                    {this.state.user.firstName ??
                      this.state.userEmail.split("@")[0]}
                    &apos;s account:
                  </h3>
                )}
                {!this.props.isAdminMode && (
                  <h3>Banks added to your account:</h3>
                )}
                {this.state.selectedBanksLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <SelectedBanksList
                      banks={this.state.selectedBanks}
                      onDelete={this.handleUnselectBankClick}
                      onEdit={(bank) => this.editBank(bank)}
                    />
                    <EditUserBankModal
                      bank={this.state.editedBank}
                      close={() => this.setState({ editedBank: null })}
                      onClosed={() => this.setState({ editedBank: null })}
                      onSubmit={this.saveBank}
                      isAdminMode={this.props.isAdminMode}
                    />
                  </>
                )}
              </Row>
              <Row>
                <Col style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    color={"primary"}
                    style={{ marginTop: "1rem" }}
                    onClick={() => this.props.history.goBack()}
                  >
                    Back
                  </Button>
                </Col>
              </Row>
            </Col>
            <Snackbar
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              open={this.state.snackBarOpen}
              onClose={this.handleCloseSnackBar}
              autoHideDuration={6000}
            >
              <div>
                <Alert color={this.state.snackBarAlertSeverity}>
                  {this.state.snackBarAlertMessage}
                </Alert>
              </div>
            </Snackbar>
          </Row>
        </Col>
      </Container>
    );
  }
}
